import React, { useState, useMemo, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView, InfoWindow } from '@react-google-maps/api';
import { getImageUrl } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface Project {
    _id: string;
    name: string;
    location: string;
    address?: string;
    priceRange?: string;
    images?: string[];
    featuredImage?: string;
    projectType?: string;
    mapDetails?: {
        latitude?: string;
        longitude?: string;
    };
    overview?: {
        priceRange?: string;
        projectArea?: string;
    };
    isOurPick?: boolean;
    slug?: string;
}

interface ProjectHeaderMapProps {
    projects: Project[];
    city?: string;
}

const mapContainerStyle = {
    width: '100%',
    height: '650px',
};

const mapOptions = {
    styles: [
        { "featureType": "administrative", "elementType": "labels.text.fill", "stylers": [{ "color": "#444444" }] },
        { "featureType": "landscape", "elementType": "all", "stylers": [{ "color": "#f2f2f2" }] },
        { "featureType": "poi", "elementType": "all", "stylers": [{ "visibility": "off" }] },
        { "featureType": "road", "elementType": "all", "stylers": [{ "saturation": -100 }, { "lightness": 45 }] },
        { "featureType": "road.highway", "elementType": "all", "stylers": [{ "visibility": "simplified" }] },
        { "featureType": "road.arterial", "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
        { "featureType": "transit", "elementType": "all", "stylers": [{ "visibility": "off" }] },
        { "featureType": "water", "elementType": "all", "stylers": [{ "color": "#cbdffd" }, { "visibility": "on" }] }
    ],
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    gestureHandling: 'cooperative'
};

const ProjectHeaderMap: React.FC<ProjectHeaderMapProps> = ({ projects, city }) => {
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const navigate = useNavigate();

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: "AIzaSyAKUwG8_kZORARJWNJeWyjT5m87jL5eZNI"
    });

    const geoProjects = useMemo(() => {
        return projects.filter(p =>
            p.mapDetails?.latitude &&
            p.mapDetails?.longitude &&
            !isNaN(parseFloat(p.mapDetails.latitude)) &&
            !isNaN(parseFloat(p.mapDetails.longitude))
        );
    }, [projects]);

    const onLoad = useCallback((m: google.maps.Map) => {
        setMap(m);
    }, []);

    const onUnmount = useCallback(() => {
        setMap(null);
    }, []);

    React.useEffect(() => {
        if (map && geoProjects.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            geoProjects.forEach(p => {
                bounds.extend({
                    lat: parseFloat(p.mapDetails!.latitude!),
                    lng: parseFloat(p.mapDetails!.longitude!)
                });
            });
            map.fitBounds(bounds);
            const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
                if ((map.getZoom() || 0) > 15) map.setZoom(15);
            });
        }
    }, [map, geoProjects]);

    const center = useMemo(() => {
        if (selectedProject) {
            return {
                lat: parseFloat(selectedProject.mapDetails!.latitude!),
                lng: parseFloat(selectedProject.mapDetails!.longitude!)
            };
        }
        if (geoProjects.length > 0) {
            const current = geoProjects[currentIndex] || geoProjects[0];
            return {
                lat: parseFloat(current.mapDetails!.latitude!),
                lng: parseFloat(current.mapDetails!.longitude!)
            };
        }
        return { lat: 21.1702, lng: 72.8311 };
    }, [geoProjects, currentIndex, selectedProject]);

    const handleNext = () => {
        if (geoProjects.length === 0) return;
        const nextIdx = (currentIndex + 1) % geoProjects.length;
        setCurrentIndex(nextIdx);
        setSelectedProject(geoProjects[nextIdx]);
    };

    const handlePrev = () => {
        if (geoProjects.length === 0) return;
        const prevIdx = (currentIndex - 1 + geoProjects.length) % geoProjects.length;
        setCurrentIndex(prevIdx);
        setSelectedProject(geoProjects[prevIdx]);
    };

    const toggleFullscreen = () => {
        if (!map) return;
        const element = map.getDiv();
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            element.requestFullscreen();
        }
    };

    if (!isLoaded) return (
        <div className="w-full h-[650px] bg-slate-100 animate-pulse flex items-center justify-center">
            <span className="text-slate-400 font-bold text-[13px]">Initializing Elite Map...</span>
        </div>
    );

    if (!city) return null;

    if (geoProjects.length === 0) {
        return (
            <div className="w-full h-[200px] bg-blue-50/30 border-b border-blue-100 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 text-blue-500">
                    <i className="fa-solid fa-map-location-dot text-2xl"></i>
                </div>
                <h6 className="text-lg font-black text-slate-900 mb-1">Project Map for {city}</h6>
                <p className="text-sm text-slate-500 font-bold max-w-md mx-auto">No projects in this city currently have map coordinates available.</p>
            </div>
        );
    }

    return (
        <div className="w-full relative shadow-2xl z-20 group h-[650px] overflow-hidden">
            <style>
                {`
                @keyframes ripple {
                    0% {
                        transform: scale(0.6);
                        opacity: 1;
                    }
                    100% {
                        transform: scale(2.8);
                        opacity: 0;
                    }
                }
                .marker-ripple {
                    position: absolute;
                    width: 30px;
                    height: 30px;
                    background: rgba(241, 174, 36, 0.4);
                    border-radius: 50%;
                    animation: ripple 2s infinite ease-out;
                    pointer-events: none;
                }
                .ripple-2 { animation-delay: 0.6s; }
                .ripple-3 { animation-delay: 1.2s; }
                `}
            </style>

            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={14}
                options={mapOptions}
                onLoad={onLoad}
                onUnmount={onUnmount}
            >
                {geoProjects.map((project, idx) => (
                    <OverlayView
                        key={project._id}
                        position={{
                            lat: parseFloat(project.mapDetails!.latitude!),
                            lng: parseFloat(project.mapDetails!.longitude!)
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                        <div 
                            className="relative flex items-center justify-center -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                            onClick={() => {
                                setSelectedProject(project);
                                setCurrentIndex(idx);
                            }}
                        >
                            {/* Pulse Rings */}
                            <div className="marker-ripple"></div>
                            <div className="marker-ripple ripple-2"></div>
                            <div className="marker-ripple ripple-3"></div>
                            
                            {/* Marker Body */}
                            <motion.div 
                                initial={false}
                                animate={{ 
                                    scale: selectedProject?._id === project._id ? 1.4 : 1,
                                    boxShadow: selectedProject?._id === project._id ? "0 0 20px rgba(0,27,74,0.4)" : "0 4px 10px rgba(0,0,0,0.15)"
                                }}
                                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 border-white transition-all bg-[#f1ae24]`}
                            >
                                <i className="fa-solid fa-house text-[14px] text-white"></i>
                            </motion.div>

                            {/* Tooltip on Hover */}
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-[#f1ae24] text-white px-3 py-1.5 rounded-lg text-[10px] font-black opacity-0 group-hover:opacity-100 transition-all pointer-events-none whitespace-nowrap shadow-xl">
                                {project.name}
                                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#f1ae24]"></div>
                            </div>
                        </div>
                    </OverlayView>
                ))}

                {selectedProject && (
                    <InfoWindow
                        position={{
                            lat: parseFloat(selectedProject.mapDetails!.latitude!),
                            lng: parseFloat(selectedProject.mapDetails!.longitude!)
                        }}
                        onCloseClick={() => setSelectedProject(null)}
                    >
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="p-0 min-w-[220px] max-w-[250px] overflow-hidden rounded-xl border-0 shadow-2xl bg-white" 
                            onClick={() => navigate(`/project-details/${selectedProject.slug || selectedProject._id}`)}
                        >
                            <div className="relative overflow-hidden h-28">
                                <img
                                    src={getImageUrl(selectedProject.featuredImage || (selectedProject.images && selectedProject.images[0]))}
                                    alt={selectedProject.name}
                                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                                />
                                {selectedProject.isOurPick && (
                                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white text-[9px] font-black rounded">
                                        Featured
                                    </div>
                                )}
                            </div>

                            <div className="p-3 bg-white">
                                <h4 className="text-xs font-black text-[#001b4a] mb-0.5 truncate">{selectedProject.name}</h4>
                                <div className="text-base font-black text-emerald-600 mb-1.5">
                                    {selectedProject.overview?.priceRange || selectedProject.priceRange || 'Starting Soon'}
                                </div>

                                <div className="flex items-center gap-3 mb-2 border-t border-slate-50 pt-2">
                                    <div className="flex items-center gap-1 grayscale opacity-70">
                                        <i className="fa-solid fa-expand text-[9px]"></i>
                                        <span className="text-[9px] font-bold text-slate-500">{selectedProject.overview?.projectArea || 'N/A'} Sq Ft</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-[8px] font-black bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">{selectedProject.projectType || 'Project'}</span>
                                    </div>
                                </div>

                                <div className="flex items-start gap-1.5">
                                    <i className="fa-solid fa-location-dot text-emerald-500 text-[9px] mt-0.5"></i>
                                    <span className="text-[10px] font-black text-black line-clamp-1 leading-snug">
                                        {selectedProject.address || selectedProject.location}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    </InfoWindow>
                )}
            </GoogleMap>

            <div className="absolute inset-0 pointer-events-none select-none flex flex-col justify-between p-6 z-30">
                <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-2 pointer-events-auto">
                        <button 
                            onClick={() => map?.setZoom((map.getZoom() || 14) + 1)}
                            className="w-10 h-10 bg-white border border-slate-200 flex items-center justify-center text-slate-800 hover:bg-[#001b4a] hover:text-white transition-all shadow-xl rounded-lg font-bold"
                        >
                            <i className="fa-solid fa-plus"></i>
                        </button>
                        <button 
                            onClick={() => map?.setZoom((map.getZoom() || 14) - 1)}
                            className="w-10 h-10 bg-white border border-slate-200 flex items-center justify-center text-slate-800 hover:bg-[#001b4a] hover:text-white transition-all shadow-xl rounded-lg font-bold"
                        >
                            <i className="fa-solid fa-minus"></i>
                        </button>
                    </div>

                    <div className="flex items-center gap-3 pointer-events-auto">
                        <div className="flex items-center bg-gradient-to-r from-[#05144b] to-[#4c1d95] rounded-xl overflow-hidden shadow-2xl">
                            <button 
                                onClick={handlePrev}
                                className="px-4 py-3 text-white border-r border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
                                disabled={geoProjects.length <= 1}
                            >
                                <i className="fa-solid fa-chevron-left text-[10px]"></i>
                                <span className="ml-2 font-black text-[11px]">Prev</span>
                            </button>
                            <button 
                                onClick={handleNext}
                                className="px-4 py-3 text-white hover:bg-white/10 transition-all disabled:opacity-50"
                                disabled={geoProjects.length <= 1}
                            >
                                <span className="mr-2 font-black text-[11px]">Next</span>
                                <i className="fa-solid fa-chevron-right text-[10px]"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end items-end">
                    <button 
                        onClick={toggleFullscreen}
                        className="px-6 py-3 bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white rounded-xl shadow-2xl font-black text-[11px] hover:shadow-indigo-500/30 transition-all pointer-events-auto flex items-center gap-3"
                    >
                        <i className="fa-solid fa-expand text-xs"></i>
                        Fullscreen
                    </button>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#f4f7fa] to-transparent pointer-events-none z-20"></div>
        </div>
    );
};

export default ProjectHeaderMap;
